<template>
  <div v-if="numPanes > 0" class="main" :class="containerClasses">
    <nav id="side-nav">
      <ul class="menu">
        <li
          v-for="(pane, index) in panes"
          :class="{'active': index === currIndex}"
          v-on:click.stop="moveToPane(pane,index)"
          :key="['nav-item', index].join('-')"
        >{{pane.title}}</li>
        <li v-on:click.stop="toggleContactPane">
          <i class="icon-envelope-closed contact"></i>
          <span class="text">Contact</span>
        </li>
        <li class="toggle-dark" @click.prevent.stop="toggleDark">
          <i class="icon" :class="toggleIcon"></i>
          <span class="label">{{toggleLabel}}</span>
        </li>
      </ul>

      <button class="menu-toggle icon-list" v-on:click="toggleMenu()"></button>
    </nav>
    <figure
      v-if="panes[currIndex].numImages >0"
      id="background-picture"
      :class="bgClass"
      v-on:click="flipBg()"
    >
      <vue-lazypic
        v-if="prevIndex > -1"
        :imgset="panes[prevIndex].image"
        :sizes="imgSizes"
        imgClass="prev-bg"
      ></vue-lazypic>
      <vue-lazypic :imgset="panes[currIndex].image" :sizes="imgSizes" imgClass="curr-bg"></vue-lazypic>
    </figure>
    <section class="panes-container" id="main-content" v-on:click="hideMenu()">
      <ul class="panes">
        <li
          v-for="(pane, index) in panes"
          :id="pane.id"
          :data-top="pane.offsetTop"
          class="pane"
          :class="pane.classNames"
          :key="['section',index,pane.id].join('-')"
        >
          <article>
            <h2>{{pane.title}}</h2>
            <div class="body" v-html="pane.body"></div>
            <template v-if="pane.has_sections">
              <div
                v-for="(section, subindex) in pane.sections"
                class="sub-section"
                :class="'sub-section-' + subindex"
                :key="['subsection',pane.id,subindex].join('-')"
              >
                <h3 v-if="section.has_title">{{section.title}}</h3>
                <div v-if="section.has_body" class="sub-body" v-html="section.body"></div>
              </div>
            </template>
          </article>
        </li>
      </ul>
    </section>
    <div id="scroll-down" class="icon-arrow-down" v-on:click="toNext()"></div>
  </div>
</template>

<script>
import _ from "lodash";
import imagesLoaded from "vue-images-loaded";
import { addBodyClass, removeBodyClass } from "../lib/utils";

function scrollY() {
  if (window.pageYOffset) {
    return window.pageYOffset;
  } else {
    return Math.max(
      document.documentElement.scrollTop,
      document.body.scrollTop
    );
  }
}

export default {
  name: "home",
  directives: {
    imagesLoaded
  },
  data: function() {
    return {
      backend: "/",
      panes: [],
      numPanes: 0,
      currIndex: 0,
      prevIndex: -1,
      prevIconTs: 0,
      bgClass: "current",
      prevY: 0,
      prevPaneY: 0,
      updating: false,
      updatingDown: false,
      showMenu: false,
      containerClasses: ["menu-collapsed"],
      imgSizes: {
        scale_crop_1024x576: "(max-width: 1024px)",
        scale_crop_1440_810: "(min-width: 1025px) AND (max-width: 1440px)",
        scale_crop_1920x1080: "(min-width: 1441px) AND (max-width: 1920px)",
        scale_crop_2880x1620: "(min-width: 1921px)"
      },
      defSize: {
        width: 1024,
        height: 576,
        name: "scale_crop_1024x576"
      }
    };
  },
  created: function() {
    this.manageDownArrow();
    window.addEventListener("scroll", this.handleScroll);
    window.addEventListener("hashchange", this.handlePop);
    this.load();
  },
  mounted: function() {
    this.$parent.$on("resized", this.calcOffsetTops);
    this.$parent.$on("escape", () => {
      this.toggleMenu("hide");
    });
  },
  computed: {
    scrolling() {
      return this.$parent.scrolling;
    },
    currPath() {
      return window.location.hash.split("--sect").shift();
    },
    toggleLabel() {
      return this.$parent.displayMode === "dark" ? "Light Mode" : "Dark Mode";
    },
    mayStore() {
      return this.$parent.mayStore;
    },
    toggleIcon() {
      return this.$parent.displayMode === "dark"
        ? "icon-sun-fill"
        : "icon-moon-fill";
    },
    hasItems() {
      let has = false;
      if (this.$parent.items) {
        if (this.$parent.items instanceof Array) {
          return this.$parent.items.length > 0;
        }
      }
      return has;
    }
  },
  methods: {
    load() {
      if (this.hasItems) {
        this.processPanes(this.$parent.items);
      } else {
        setTimeout(this.load, 1000);
      }
    },
    processPanes(items) {
      this.panes = [];
      items.forEach((item, i) => {
        this.processPane(item, i);
      });
      this.numPanes = this.panes.length;

      setTimeout(() => {
        this.$parent.handleResize(true);
        removeBodyClass("loading");
        addBodyClass("loaded");
      }, 240);
      setTimeout(() => {
        this.moveToCurrPane();
        if (SvgMorph) {
          SvgMorph.init();
          setTimeout(this.morphIcon, 6000);
        }
      }, 360);
    },
    processPane(row, index) {
      if (typeof row === "object") {
        if (row.title) {
          const pane = {
            title: row.title,
            body: "",
            numImages: 0,
            offsetTop: 0,
            classNames: ["bg-" + ((index % 6) + 1)]
          };
          if (index === this.currIndex) {
            pane.classNames.push("active");
          }
          if (row.path) {
            pane.id = row.path.replace(/^\//, "").replace(/\//, "--");
            pane.path = row.path;
          }
          if (row.images) {
            pane.numImages = row.images.length;
            const randIndex = Math.floor(
              Math.random() * pane.numImages * 0.9999
            );
            const image = row.images[randIndex];
            if (image.size) {
              image.size.width = this.defSize.width;
              image.size.height = this.defSize.height;
            }
            pane.image = image;
          }
          if (row.body) {
            pane.body = row.body;
          }
          pane.has_sections = false;
          pane.sections = [];
          if (row.has_sections) {
            pane.has_sections = row.has_sections;
            pane.sections = row.sections;
          }
          this.panes.push(pane);
        }
      }
    },
    changeAlias: function(tgPath, index) {
      const paneIndex = this.panes.findIndex(p => p.id === tgPath.substring(1));
      if (paneIndex >= 0) {
        index = paneIndex;
      }

      window.location = tgPath + "--section";

      if (this.panes[this.currIndex].classNames.length > 1) {
        this.panes[this.currIndex].classNames.pop();
      }
      this.prevIndex = this.currIndex;
      if (index < 0) {
        index = 0;
      }
      this.currIndex = index;
      for (var i = 0; i < this.$parent.classNames.length; i++) {
        if (/^bg-\d+/.test(this.$parent.classNames[i])) {
          this.$parent.classNames[i] = this.panes[index].classNames[0];
        }
      }
      this.panes[index].classNames.push("active");
      this.bgClass = "switching";
      setTimeout(() => {
        this.bgClass = "current";
      }, 1900);
    },
    moveToPane(pane, index) {
      let offsetY = 0;
      this.hideMenu();
      if (pane.offsetTop) {
        offsetY = pane.offsetTop;
      }
      if (offsetY < this.$parent.stopY * 16) {
        offsetY = 0;
      }
      if (offsetY < this.$parent.stopY * 3) {
        this.toggleParentClass("scrolled-down", "rem");
      }
      const tgPath = "#" + pane.path;

      this.toggleMenu("hide");
      let wOfs = 0;
      if (offsetY > this.$parent.halfY) {
        wOfs = this.$parent.winWidth / 50;
        if (wOfs > 32) {
          wOfs = 32;
        }
        wOfs = this.$parent.stopY * 2 + wOfs;
      }

      let ms = 1000;
      if (pane.hasOwnProperty("offsetTop")) {
        const diff = Math.abs(scrollY() - pane.offsetTop);
        ms = Math.pow(diff / this.$parent.winHeight, 0.5) * 1000;
      }
      const target = pane.id;

      this.$parent.scrollTo("#" + target, wOfs, ms);
      this.morphIcon();
      if (tgPath !== this.currPath) {
        setTimeout(() => {
          this.changeAlias(tgPath, index);
        }, 1000);
      }
    },
    toNext() {
      const nextIndex = this.currIndex + 1;
      if (nextIndex < this.numPanes) {
        const pane = this.panes[nextIndex];
        if (pane.title) {
          this.moveToPane(pane, nextIndex);
        }
      }
    },
    flipBg() {
      this.bgClass = "switching";
      if (this.prevIndex > -1) {
        setTimeout(() => {
          this.toggleContainerClass("flip-x");
          setTimeout(() => {
            this.bgClass = "current";
          }, 900);
        }, 100);
      }
    },
    morphIcon() {
      if (SvgMorph) {
        const currTs = new Date().getTime();
        if (this.prevIconTs < currTs - 3 * 1000) {
          SvgMorph.morphIcon();
          this.prevIconTs = currTs;
        }
      }
    },
    moveToCurrPane() {
      const currId = this.currPath.replace("#/", "").replace(/\//g, "--");
      const paneIndex = this.panes.findIndex(p => p.id === currId);
      if (paneIndex >= 0) {
        this.moveToPane(this.panes[paneIndex], paneIndex);
      }
    },
    manageDownArrow() {
      if (!this.updatingDown) {
        this.updatingDown = true;
        if (this.panes.length > 1) {
          const lastOffset = this.panes[this.panes.length - 1].offsetTop;
          const md =
            scrollY() > lastOffset - this.$parent.halfY ? "rem" : "add";
          this.toggleParentClass("show-down", md);
          this.removeContainerClass("hide-arrow");
          if (md === "add") {
            setTimeout(() => {
              this.addContainerClass("hide-arrow");
            }, 5000);
          }
          setTimeout(() => {
            this.updatingDown = false;
          }, 1100);
        }
      }
    },
    handleScroll() {
      const sy = scrollY();
      if (
        sy > this.prevY + this.$parent.stopY ||
        scrollY() < this.prevY - this.$parent.stopY
      ) {
        this.manageDownArrow();
        if (scrollY() > this.$parent.stopY * 6) {
          this.toggleParentClass("scrolled-down", "add");
        } else {
          this.toggleParentClass("scrolled-down", "rem");
        }
        if (this.numPanes > 0 && this.updating === false) {
          const isLast =
            sy > this.panes[this.numPanes - 1].offsetTop - this.$parent.stopY;
          const start = isLast ? this.numPanes - 1 : 0;
          let pn = null;
          const sTop = sy - this.$parent.winHeight / 2;
          let paneIndex = this.panes.findIndex(pn => pn.offsetTop >= sTop);
          if (paneIndex >= 0) {
            const pane = this.panes[paneIndex];
            this.moveToInnerSection(pane, paneIndex, isLast);
          }
        }
        this.prevY = sy;
        if (this.containerClasses.indexOf("moving") < 0) {
          setTimeout(() => {
            this.removeContainerClass("moving");
          }, 500);
        }
        this.addContainerClass("moving");
      }
    },
    moveToInnerSection(pane, paneIndex, isLast) {
      const index = paneIndex > 0 && !isLast ? paneIndex - 1 : paneIndex;
      const tgPath = "#" + pane.path;
      if (tgPath !== this.currPath) {
        this.updating = true;
        this.prevPaneY = scrollY();

        this.changeAlias(tgPath, index);
        setTimeout(() => {
          this.updating = false;
        }, 250);
        this.morphIcon();
      }
    },
    handlePop(e) {
      if (e.oldURl !== e.newURL) {
        const path = window.location.hash
          .replace(/^#/, "")
          .split("--")
          .shift();
        if (path.length > 0) {
          if (this.scrolling === false) {
            if (path !== this.currPath) {
              const index = this.panes.findIndex(pane => pane.path === path);
              if (index >= 0) {
                this.moveToPane(this.panes[index], index);
              }
            }
          }
        }
      }
    },
    calcOffsetTops() {
      for (let i = 0; i < this.numPanes; i++) {
        let elem = document.getElementById(this.panes[i].id);
        if (elem) {
          this.panes[i].offsetTop = elem.offsetTop;
        }
      }
    },
    hideMenu() {
      this.toggleMenu("hide");
    },
    toggleMenu(mode) {
      switch (mode) {
        case "hide":
          this.showMenu = true;
          break;
        case "show":
          this.showMenu = false;
          break;
      }
      if (this.showMenu) {
        this.removeContainerClass("menu-expanded");
        this.addContainerClass("menu-collapsed");
        this.toggleParentClass("show-menu", "rem");
        this.showMenu = false;
      } else {
        this.removeContainerClass("menu-collapsed");
        this.addContainerClass("menu-expanded");
        this.toggleParentClass("show-menu", "add");
        this.showMenu = true;
      }
    },
    addContainerClass: function(clsName) {
      this.toggleContainerClass(clsName, "add");
    },
    removeContainerClass: function(clsName) {
      this.toggleContainerClass(clsName, "rem");
    },
    toggleParentClass: function(clsName, mode) {
      this.toggleContainerClass(clsName, mode, true);
    },
    toggleContainerClass: function(clsName, mode, parent) {
      let cls = [];
      if (parent === true) {
        cls = this.$parent.extraClasses;
      } else {
        cls = this.containerClasses;
      }
      const index = cls.indexOf(clsName);
      if (!mode) {
        mode = index < 0 ? "add" : "rem";
      }
      if (mode === "add" && index < 0) {
        cls.push(clsName);
      } else if (index >= 0) {
        switch (mode) {
          case "rem":
          case "remove":
            cls.splice(index, 1);
            break;
        }
      }
    },
    toggleContactPane(mode) {
      this.$parent.toggleContactPane(mode);
    },
    toggleDark() {
      this.$parent.toggleDark();
    }
  }
};
</script>




// WEBPACK FOOTER //
// Home.vue?82407f36